import { Fragment, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface MonthCell {
  leads: number;
  deals: number;
  gross: number;
  subscribers: number | null;
}
interface ChannelData {
  key: string;
  label: string;
  hasSubscribers: boolean;
  byMonth: Record<string, MonthCell>;
}
interface ChannelNumbersResponse {
  fiscalYearLabel: string;
  months: string[];
  channels: ChannelData[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (ym: string) => {
  const [y, m] = ym.split("-");
  return `${MONTHS[Number(m) - 1]} ${y?.slice(2)}`;
};
const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
const fmtUsd = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
const fmtRate = (deals: number, leads: number) =>
  leads > 0 ? `${((deals / leads) * 100).toFixed(1)}%` : "—";

/**
 * Company-wide results for the lead channels tied to this person (e.g. Raleigh:
 * YouTube + Diamond Dude TikTok) — leads, closed deals, close rate, gross sales,
 * and YouTube subscribers, by month with quarter + year totals. Renders nothing
 * for people without channels configured.
 */
export function ChannelNumbersCard({
  ownerId,
  fiscalYear,
}: {
  ownerId: string;
  fiscalYear: number;
}) {
  const [data, setData] = useState<ChannelNumbersResponse | null>(null);

  useEffect(() => {
    fetch(`/api/owners/${ownerId}/channel-numbers?fiscalYear=${fiscalYear}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d?.channels?.length ? d : null))
      .catch(() => setData(null));
  }, [ownerId, fiscalYear]);

  if (!data) return null;
  const now = new Date();
  const nowYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const quarters = [0, 1, 2, 3].map((q) => ({
    label: `Q${q + 1}`,
    months: data.months.slice(q * 3, q * 3 + 3),
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Channel Numbers ({data.fiscalYearLabel})</CardTitle>
        <p className="text-xs text-muted-foreground">
          Company-wide results for these channels — every lead and sale that came from them, not
          only your own deals.
        </p>
      </CardHeader>
      <CardContent className="grid gap-6 xl:grid-cols-2">
        {data.channels.map((c) => {
          const sum = (months: string[]) =>
            months.reduce(
              (a, m) => {
                const cell = c.byMonth[m];
                return cell
                  ? {
                      leads: a.leads + cell.leads,
                      deals: a.deals + cell.deals,
                      gross: a.gross + cell.gross,
                    }
                  : a;
              },
              { leads: 0, deals: 0, gross: 0 },
            );
          // Latest subscriber snapshot within a set of months.
          const lastSubs = (months: string[]) => {
            for (const m of [...months].reverse()) {
              const v = c.byMonth[m]?.subscribers;
              if (v != null) return v;
            }
            return null;
          };
          const year = sum(data.months);
          const yearSubs = lastSubs(data.months);
          return (
            <div key={c.key} className="overflow-x-auto">
              <p className="mb-2 font-heading text-lg">{c.label}</p>
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-muted-foreground">
                    <th className="py-2 pr-2 text-left font-medium">Month</th>
                    <th className="px-2 py-2 text-right font-medium">Leads</th>
                    <th className="px-2 py-2 text-right font-medium">Closed</th>
                    <th className="px-2 py-2 text-right font-medium">Close rate</th>
                    <th className="px-2 py-2 text-right font-medium">Gross sales</th>
                    {c.hasSubscribers && (
                      <th className="py-2 pl-2 text-right font-medium">Subscribers</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {quarters.map((q) => {
                    const t = sum(q.months);
                    const started = (q.months[0] ?? "") <= nowYm;
                    const qSubs = lastSubs(q.months);
                    return (
                      <Fragment key={q.label}>
                        {q.months.map((m) => {
                          const cell = c.byMonth[m];
                          const future = m > nowYm;
                          return (
                            <tr key={m} className="border-b border-border/40">
                              <td className="py-1.5 pr-2 text-muted-foreground">{fmtMonth(m)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {future || !cell ? "" : fmtInt(cell.leads)}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {future || !cell ? "" : fmtInt(cell.deals)}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {future || !cell ? "" : fmtRate(cell.deals, cell.leads)}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {future || !cell ? "" : fmtUsd(cell.gross)}
                              </td>
                              {c.hasSubscribers && (
                                <td className="py-1.5 pl-2 text-right tabular-nums">
                                  {cell?.subscribers != null
                                    ? fmtInt(cell.subscribers)
                                    : future
                                      ? ""
                                      : "—"}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        <tr
                          className={cn(
                            "border-b bg-muted/30 font-semibold",
                            !started && "opacity-50",
                          )}
                        >
                          <td className="py-1.5 pr-2">{q.label} total</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmtInt(t.leads)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmtInt(t.deals)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {fmtRate(t.deals, t.leads)}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmtUsd(t.gross)}</td>
                          {c.hasSubscribers && (
                            <td className="py-1.5 pl-2 text-right tabular-nums">
                              {qSubs != null ? fmtInt(qSubs) : "—"}
                            </td>
                          )}
                        </tr>
                      </Fragment>
                    );
                  })}
                  <tr className="border-t-2 font-semibold">
                    <td className="py-2 pr-2">Year</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtInt(year.leads)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtInt(year.deals)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {fmtRate(year.deals, year.leads)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtUsd(year.gross)}</td>
                    {c.hasSubscribers && (
                      <td className="py-2 pl-2 text-right tabular-nums">
                        {yearSubs != null ? fmtInt(yearSubs) : "—"}
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
